import React, { useEffect, useState } from 'react';
import {
  Box, Flex, HStack, Text, useColorModeValue,
} from '@chakra-ui/react';

export interface Segment {
  label: string;
  value: number;
  color: string; // a theme token, e.g. 'brandGreen.400'
}

/**
 * A real proportion bar (not a fake chart): one rounded track split into
 * segments sized by their share of the total, each glowing in its color, with
 * a legend below. Widths grow in on mount. Used for the pipeline breakdown.
 */
const SegmentBar: React.FC<{ segments: Segment[] }> = ({ segments }) => {
  const [grown, setGrown] = useState(false);
  const trackBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const total = segments.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <Box>
      <Flex h="8px" borderRadius="full" overflow="hidden" bg={trackBg} gap="2px">
        {segments.map((s) => (
          <Box
            key={s.label}
            bg={s.color}
            borderRadius="full"
            w={grown ? `${(s.value / total) * 100}%` : '0%'}
            boxShadow={`0 0 10px var(--chakra-colors-${s.color.replace('.', '-')})`}
            transition="width 0.9s cubic-bezier(0.22, 1, 0.36, 1)"
          />
        ))}
      </Flex>
      <HStack mt={3} spacing={4} flexWrap="wrap">
        {segments.map((s) => (
          <HStack key={s.label} spacing={1.5}>
            <Box w="8px" h="8px" borderRadius="full" bg={s.color} />
            <Text fontSize="xs" color="gray.500">
              {s.label}
              {' '}
              <Box as="span" fontWeight="bold" color="inherit">{s.value}</Box>
            </Text>
          </HStack>
        ))}
      </HStack>
    </Box>
  );
};

export default SegmentBar;
